/* Public one-tap approval page for an Amazon auto-order (link arrives by text/email). */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { ShoppingCart, CheckCircle2, XCircle, Loader2, Shield } from "lucide-react";

export default function ApproveOrder() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState("");

  useEffect(() => {
    api.get(`/auto-order/approval/${token}`).then((r) => setData(r.data)).catch((e) => setErr(formatErr(e.response?.data?.detail) || "This link isn't valid anymore."));
  }, [token]);

  const act = async (kind) => {
    setBusy(kind); setErr("");
    try {
      const { data: r } = await api.post(`/auto-order/approval/${token}/${kind}`);
      setData((d) => ({ ...d, order: r.order }));
      setDone(kind === "approve" ? (r.already ? "already" : "approved") : "declined");
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(""); }
  };

  const o = data?.order;
  const open = o?.status === "awaiting_approval" && !data?.expired && !done;
  return (
    <main className="min-h-screen bg-cream text-ink flex items-center justify-center px-6 py-12" data-testid="approve-order-page">
      <div className="w-full max-w-md">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-sage mb-6"><Shield className="w-4 h-4" /> Withdraw Guard · Inventory</div>
        {!data && !err && <Loader2 className="w-6 h-6 animate-spin text-moss" />}
        {err && !o && <p className="text-clay" data-testid="approve-order-error">{err}</p>}
        {o && (
          <div className="bg-linen border border-sand rounded-2xl p-6 shadow-sm">
            <div className="inline-flex items-center gap-1.5 text-xs text-moss"><ShoppingCart className="w-3.5 h-3.5" /> Amazon auto-order</div>
            <h1 className="font-serif text-3xl mt-2 leading-tight" data-testid="approve-order-title">{o.item_name} <span className="text-moss">×{o.qty}</span></h1>
            {o.title && o.title !== o.item_name && <p className="text-sm text-moss mt-1">{o.title}</p>}
            <div className="mt-5 flex items-baseline justify-between border-t border-sand pt-4">
              <span className="text-sm text-moss">From your Restock pot</span>
              <span className="font-mono text-2xl" data-testid="approve-order-amount">~{fmtMoney(o.reserved)}</span>
            </div>
            <p className="text-[11px] text-moss mt-1">Ceiling incl. tax &amp; shipping — Amazon charges only the real total.</p>

            {open && (
              <div className="mt-6 grid grid-cols-1 gap-3">
                <button onClick={() => act("approve")} disabled={!!busy} className="bg-forest text-white rounded-full py-3.5 text-base font-semibold hover:bg-forest-deep disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="approve-order-yes">
                  {busy === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Yes, buy it
                </button>
                <button onClick={() => act("decline")} disabled={!!busy} className="border border-sand rounded-full py-3 text-sm text-moss hover:bg-sand-light disabled:opacity-50" data-testid="approve-order-no">Not now</button>
                {o.expires_at && <p className="text-[11px] text-moss text-center">Link expires {new Date(o.expires_at).toLocaleString()}</p>}
              </div>
            )}
            {!open && (
              <div className="mt-6 text-sm" data-testid="approve-order-result">
                {done === "approved" || o.status === "processing" || o.status === "placing" ? <p className="text-forest inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Ordering now — your receipt will arrive by email once Amazon confirms.</p>
                  : o.status === "ordered" ? <p className="text-forest inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Already ordered{o.merchant_order_ids?.length ? ` · #${o.merchant_order_ids.join(", ")}` : ""}.</p>
                  : o.status === "declined" ? <p className="text-moss inline-flex items-center gap-2"><XCircle className="w-4 h-4" /> Skipped. It'll ask again next time the item is updated while low.</p>
                  : data?.expired || o.status === "expired" ? <p className="text-clay">This request expired. It'll ask again next time the item is updated while low.</p>
                  : o.status === "failed" ? <p className="text-clay">The order didn't go through{o.error ? ` — ${o.error}` : ""}.</p>
                  : null}
              </div>
            )}
            {err && o && <p className="mt-3 text-sm text-clay" data-testid="approve-order-error">{err}</p>}
          </div>
        )}
        <p className="mt-6 text-xs text-moss"><Link to="/inventory" className="underline hover:text-ink">Open Inventory</Link> to manage auto-ordering.</p>
      </div>
    </main>
  );
}
